import {
  createContext,
  useCallback,
  useContext,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePlanInfo, startTrial } from "@/hooks/usePlanInfo";
import { supabase } from "@/integrations/supabase/client";
import type { PlanInfo } from "@/types/billing";

export type PlanContextValue = PlanInfo & {
  isLoading: boolean;
  refresh: () => Promise<void>;
  startTrialForUser: () => Promise<{ error?: string }>;
};

const PlanContext = createContext<PlanContextValue | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const plan = usePlanInfo();

  const startTrialForUser = useCallback(async () => {
    if (!user) {
      return { error: "Zaloguj się, aby uruchomić trial." };
    }
    const r = await startTrial(supabase, user.id);
    if (!r.error) {
      await plan.refresh();
    }
    return r;
  }, [user, plan]);

  const value: PlanContextValue = {
    ...plan,
    startTrialForUser,
  };

  return (
    <PlanContext.Provider value={value}>{children}</PlanContext.Provider>
  );
}

export function usePlan(): PlanContextValue {
  const ctx = useContext(PlanContext);
  if (!ctx) {
    throw new Error("usePlan musi być użyte wewnątrz PlanProvider.");
  }
  return ctx;
}
